/**
 * AxisChangeWarningDialog — warns when an axis change will mismatch existing edges.
 *
 * Lists each affected connection with its current type and proposed conversion.
 * "Continue" auto-converts all, "Cancel" aborts the save.
 */

import { AlertTriangle } from 'lucide-react';

export interface AffectedEdge {
  targetNodeName: string;
  currentType: 'parent-child' | 'related';
  proposedType: 'parent-child' | 'related';
}

interface AxisChangeWarningDialogProps {
  nodeName: string;
  newAxis: string;
  affectedEdges: AffectedEdge[];
  onContinue: () => void;
  onCancel: () => void;
}

export function AxisChangeWarningDialog({
  nodeName,
  newAxis,
  affectedEdges,
  onContinue,
  onCancel,
}: AxisChangeWarningDialogProps) {
  return (
    <>
      {/* Backdrop — fixed + z-[1300] to render above NodeModal (z-1100/z-1200) */}
      <div className="fixed inset-0 z-[1300]" style={{ background: 'var(--modal-backdrop)' }} onClick={onCancel} />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        className="fixed top-1/2 left-1/2 z-[1400] w-96 rounded-2xl"
        style={{
          transform: 'translate(-50%, -50%)',
          background: 'var(--modal-bg-heavy)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(245, 158, 11, 0.35)',
          boxShadow: 'var(--modal-shadow), 0 0 40px rgba(245,158,11,0.1)',
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 p-4 pb-3" style={{ borderBottom: '1px solid var(--glass-border)' }}>
          <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Axis Change Warning
          </h3>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
            Moving <strong style={{ color: 'var(--text-primary)' }}>{nodeName}</strong> to{' '}
            <strong style={{ color: 'var(--text-primary)' }}>{newAxis}</strong> will
            affect {affectedEdges.length} connection{affectedEdges.length !== 1 ? 's' : ''}:
          </p>

          <div
            className="max-h-48 overflow-y-auto rounded-lg"
            style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
          >
            {affectedEdges.map((edge, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-3 py-2 text-[11px]"
                style={{
                  borderBottom: i < affectedEdges.length - 1 ? '1px solid var(--glass-border)' : undefined,
                }}
              >
                <span className="flex-1 truncate" style={{ color: 'var(--text-secondary)' }}>
                  {edge.targetNodeName}
                </span>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{
                    background: edge.currentType === 'parent-child' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)',
                    color: edge.currentType === 'parent-child' ? '#3b82f6' : '#f59e0b',
                  }}
                >
                  {edge.currentType === 'parent-child' ? 'parent-child' : 'related'}
                </span>
                <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>&rarr;</span>
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{
                    background: edge.proposedType === 'parent-child' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)',
                    color: edge.proposedType === 'parent-child' ? '#3b82f6' : '#f59e0b',
                  }}
                >
                  {edge.proposedType === 'parent-child' ? 'parent-child' : 'related'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-3" style={{ borderTop: '1px solid var(--glass-border)' }}>
          <button
            onClick={onCancel}
            className="text-[11px] px-3 py-1.5 rounded-lg transition-colors"
            style={{
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              color: 'var(--text-secondary)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onContinue}
            className="text-[11px] px-3 py-1.5 rounded-lg transition-colors"
            style={{
              background: 'rgba(245,158,11,0.2)',
              border: '1px solid rgba(245,158,11,0.4)',
              color: '#f59e0b',
            }}
          >
            Continue &amp; Convert
          </button>
        </div>
      </div>
    </>
  );
}
